import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import { TravelTypes } from '../../../../constants';
import AddUpdateBrochure from '..';
import getBrochureTripId from '../../../../helpers/getBrochureTripId';
import useHandleCarRentalAddToBrochure from '../../../../hooks/useHandleCarRentalAddToBrochure';
import arePassengerCountsEqual from '../../../../helpers/checkArePassengerCountsEqual';

const RentalCarAddUpdateToBrochure = ({
  tripId,
  carItinerary,
  selectedTab,
  className,
  tripRoomSearchInput,
}) => {
  const router = useRouter();
  const { brochure } = useSelector((state) => state.brochure);
  const [isSelected, setIsSelected] = useState(false);
  useAnalytics();
  const { staterooms: stateroomsObj } = useSelector((store) => store.tripSearch.searchObj);

  const { isViewingBrochure } = useSelector((state) => state.brochure);
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const id = useMemo(
    () =>
      getBrochureTripId(
        tripId,
        brochure,
        carItinerary,
        tripRoomSearchInput || stateroomsObj,
        TravelTypes.carrental,
      ),
    [brochure, tripRoomSearchInput, carItinerary, stateroomsObj, tripId],
  );

  const findRentalCarItinerary = (tripSegments, uniqueKey, staterooms) =>
    _.find(
      tripSegments,
      (ts) =>
        ts.itinerary.uniqueKey === uniqueKey &&
        ts.itinerary?.startDateTime === carItinerary?.startDateTime &&
        ts.itinerary?.endDateTime === carItinerary?.endDateTime &&
        arePassengerCountsEqual(staterooms, tripRoomSearchInput || stateroomsObj),
    );

  const findRentalCarItineraries = (tripSegments) =>
    _.filter(tripSegments, (ts) => ts.itinerary.mode === TravelTypes.carrental);

  const currentBrochure = useSelector((state) => state.brochure.brochures[brochure && brochure.id]);
  const brochureOnBrochurePage = useSelector((state) => state.brochure.brochures[tripId]);

  const setSelectedTripSegment = useCallback(
    (tripSegments, staterooms) => {
      if (
        tripSegments &&
        tripSegments.length &&
        findRentalCarItinerary(tripSegments, carItinerary.uniqueKey, staterooms) != null
      ) {
        setIsSelected(true);
      }
      // oxlint-disable-next-line react/exhaustive-deps
    },
    [carItinerary.uniqueKey],
  );

  useEffect(() => {
    setIsSelected(false);

    const findAndSetTripSegments = (brochureData, tripIdentifier) => {
      if (!brochureData || !brochureData.trips || brochureData.trips.length === 0) {
        return;
      }

      const isMatchingCarSegment = (segment, trip) => {
        const itinerary = segment?.itinerary;

        return (
          itinerary?.mode === TravelTypes.carrental &&
          itinerary?.uniqueKey === carItinerary?.uniqueKey &&
          itinerary?.startDateTime === carItinerary?.startDateTime &&
          itinerary?.endDateTime === carItinerary?.endDateTime &&
          arePassengerCountsEqual(tripRoomSearchInput, trip?.roomSearchInput ?? trip?.staterooms)
        );
      };

      const isMatchingTrip = (trip) =>
        trip.id === tripIdentifier &&
        trip?.tripSegments?.some((segment) => isMatchingCarSegment(segment, trip));

      const foundTrip = brochureData.trips.find(isMatchingTrip);

      if (foundTrip && foundTrip.tripSegments) {
        setSelectedTripSegment(
          foundTrip.tripSegments,
          foundTrip?.staterooms ?? foundTrip?.roomSearchInput,
        );
      }
    };

    if (isBrochurePage && tripId) {
      findAndSetTripSegments(brochureOnBrochurePage, tripId);
      return;
    }

    if (isViewingBrochure && currentBrochure && brochure && brochure.id) {
      findAndSetTripSegments(currentBrochure, brochure.id);
      return;
    }

    if (brochure && brochure.trips && brochure.trips[id]) {
      const tripSegments = findRentalCarItineraries(brochure.trips[id].tripSegments);
      if (tripSegments && tripSegments.length > 0) {
        setSelectedTripSegment(
          tripSegments,
          brochure?.trips?.[id]?.staterooms ?? brochure?.trips?.[id]?.roomSearchInput,
        );
      }
    }
  }, [
    brochure,
    brochureOnBrochurePage,
    currentBrochure,
    carItinerary,
    isBrochurePage,
    isViewingBrochure,
    setSelectedTripSegment,
    tripId,
    id,
    stateroomsObj,
    tripRoomSearchInput,
  ]);

  const handleAddToBrochureClick = useHandleCarRentalAddToBrochure({
    tripId,
    carItinerary,
    brochure,
    isViewingBrochure,
    isBrochurePage,
    currentBrochure,
    brochureOnBrochurePage,
    selectedTab,
    tripRoomSearchInput,
  });
  return (
    <AddUpdateBrochure
      handleAddToBrochureClick={handleAddToBrochureClick}
      className={className}
      isSelected={isSelected}
      tripId={tripId}
    />
  );
};

RentalCarAddUpdateToBrochure.propTypes = {
  className: PropTypes.string,
  tripId: PropTypes.string.isRequired,
  selectedTab: PropTypes.number.isRequired,
  carItinerary: PropTypes.shape().isRequired,
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
};

RentalCarAddUpdateToBrochure.defaultProps = {
  className: '',
  tripRoomSearchInput: undefined,
};
export default RentalCarAddUpdateToBrochure;
